import {
  getOptionsForDynamicWarehouseTable,
  getAutoGeneratedAccountIdValue,
  shouldDisableSelectDropdown,
} from '../../../../../services/accountIdService';
import SimpleInput from '../../../../common/SimpleInput/SimpleInput';
import styles from '../FormStyles.module.scss';
import Select from '../../../../common/Select';
import { DynamicTableUpdateStateHelper } from '../helpers/dynamicTableUpdateStateHelper';
import {
  AUTO_GENERATE_ACCOUNT_ID,
  CUSTOM_DIVISION_CODE_MAPPING,
  DIRECT_DIVISION_CODE_MAPPING,
  SECOND_ACCOUNT_ID,
  SECOND_ACCOUNT_ID_TYPES,
  STATIC_ACCOUNT_ID,
} from '../../../../../store/constants/accountIdConfigTypes';
import { InfoTitle } from '../../../../common/InfoTooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import KeyValueRows from '../../../../common/KeyValueRows/KeyValueRows';
import { CustomAccountIdMappingHelper } from '../helpers/customAccountIdMappingHelper';

const TABLE_HEADERS = ['Warehouse', 'Type + Account Id'];
export function WarehouseTable({ props, form, setForm }) {
  const options = getOptionsForDynamicWarehouseTable(props);
  const updateStateHelper = new DynamicTableUpdateStateHelper({ form, setForm });
  const customAccountIdMappingHelper = new CustomAccountIdMappingHelper({ form, setForm });

  if (!props.warehouses) {
    console.warn('There are no relevant warehouses');
    return <></>;
  }

  return (
    <table className={styles.table_wrap}>
      <tbody>
        <tr>
          {TABLE_HEADERS.map((tableHeader, idx) => (
            <th key={idx}>{tableHeader}</th>
          ))}
        </tr>
        {options.map((option, idx) => (
          <tr key={idx}>
            <td>{option.name}</td>
            <td className={styles.type_and_account_id_column}>
              <InfoTitle
                content={
                  <p>
                    Only the <b>{updateStateHelper.getLabelOfSelectedType(form, option)}</b> account id type is
                    supported for this warehouse integration.
                  </p>
                }
                disabled={!shouldDisableSelectDropdown(option.accountIdTypes)}
              >
                <div className={styles.select_wrapper_div}>
                  <Select
                    value={updateStateHelper.getValueOfSelectedType(form, option)}
                    name={`mapping.${option.id}.type`}
                    options={option.accountIdTypes}
                    onChange={(val) => updateStateHelper.updateSelectedAccountIdType(val, option.id)}
                    disabled={shouldDisableSelectDropdown(option.accountIdTypes)}
                  />
                </div>
              </InfoTitle>
              {RenderAutoGenerateAccountIdTooltipIfNeeded(option, props)}
              {SECOND_ACCOUNT_ID_TYPES.includes(updateStateHelper.getValueOfSelectedType(form, option)) && (
                <div className={styles.two_account_ids_with_flex}>
                  <label>
                    <SimpleInput
                      value={updateStateHelper.getValueOfSecondaryAccountId(option.id, 0)}
                      placeholder="my-account-id-1"
                      onChange={(e) => {
                        updateStateHelper.updateSecondaryAccountIds({
                          accountIdToUpdate: e.target.value,
                          warehouseId: option.id,
                          indexOfAccountIdToUpdate: 0,
                          selectedType: updateStateHelper.getValueOfSelectedType(form, option),
                          secondAccountIdConfig: option.secondAccountIdConfig,
                        });
                      }}
                    />
                    <span className={styles.required_asterisk}></span>
                  </label>
                  <label>
                    <SimpleInput
                      value={updateStateHelper.getValueOfSecondaryAccountId(option.id, 1)}
                      placeholder="my-account-id-2"
                      onChange={(e) =>
                        updateStateHelper.updateSecondaryAccountIds({
                          accountIdToUpdate: e.target.value,
                          warehouseId: option.id,
                          indexOfAccountIdToUpdate: 1,
                          selectedType: updateStateHelper.getValueOfSelectedType(form, option),
                          secondAccountIdConfig: option.secondAccountIdConfig,
                        })
                      }
                    />
                    {updateStateHelper.getValueOfSelectedType(form, option) === SECOND_ACCOUNT_ID ? (
                      <span className={styles.required_asterisk}></span>
                    ) : (
                      ''
                    )}
                  </label>
                </div>
              )}

              {[STATIC_ACCOUNT_ID, AUTO_GENERATE_ACCOUNT_ID].includes(
                updateStateHelper.getValueOfSelectedType(form, option),
              ) && (
                <div className={styles.static_account_id}>
                  <label>
                    <SimpleInput
                      value={updateStateHelper.getValueOfStaticAccountId(option.id)}
                      placeholder={`my-account-id`}
                      onChange={(e) =>
                        updateStateHelper.updateStaticAccountId(e.target.value, option.id, {
                          type: updateStateHelper.getValueOfSelectedType(form, option),
                        })
                      }
                    />
                    <span className={styles.required_asterisk}></span>
                  </label>
                </div>
              )}

              {updateStateHelper.getValueOfSelectedType(form, option) === DIRECT_DIVISION_CODE_MAPPING && (
                <div className={styles.direct_division_code_info}>
                  <InfoTitle content={<div>We will set the account id from the sales division code</div>}>
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </InfoTitle>
                  <span>Automatic</span>
                </div>
              )}
              {updateStateHelper.getValueOfSelectedType(form, option) === CUSTOM_DIVISION_CODE_MAPPING && (
                <KeyValueRows
                  keyPlaceholder="key_name_on_order"
                  value={customAccountIdMappingHelper.getValueOfCustomDivisionCodeMapping(option.id)}
                  onKeyChange={(...params) => customAccountIdMappingHelper.onKeyChange(...params, option.id)}
                  onMappingKeyChange={(...params) =>
                    customAccountIdMappingHelper.onMappingKeyChange(...params, option.id)
                  }
                  onMappingValueChange={(...params) =>
                    customAccountIdMappingHelper.onMappingValueChange(...params, option.id)
                  }
                  onAddKeyValueRowClick={(...params) =>
                    customAccountIdMappingHelper.onAddKeyValueRowClick(...params, option.id)
                  }
                  onRemove={(...params) => customAccountIdMappingHelper.onRemove(...params, option.id)}
                />
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RenderAutoGenerateAccountIdTooltipIfNeeded(option, props) {
  const autoGeneratedAccountIdValue = getAutoGeneratedAccountIdValue(option, props.accountIdConfig);
  if (!autoGeneratedAccountIdValue) return <></>;

  return (
    <div className={styles.auto_generate_account_id_tooltip}>
      <InfoTitle
        content={
          <div>
            <p>
              <b>Note: </b>There is currently a generated value from this ID{' '}
            </p>
            <pre>{JSON.stringify(autoGeneratedAccountIdValue)}</pre>
          </div>
        }
      >
        <FontAwesomeIcon icon={faInfoCircle} />
      </InfoTitle>
    </div>
  );
}
